import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
    Background, Controls, MiniMap, useNodesState, useEdgesState,
    MarkerType, Panel, addEdge, Handle, Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DISCBadge from './DISCBadge';
import { Thermometer, Layout, ZoomIn, Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Custom Node ────────────────────────────────────────────────────────────

const ROLE_COLORS = {
    Champion: 'border-emerald-400',
    'Decision Maker': 'border-stone-600',
    'Economic Buyer': 'border-purple-400',
    Influencer: 'border-blue-400',
    Blocker: 'border-red-400',
    User: 'border-stone-300',
};

const STRENGTH_BG = {
    strong: 'bg-emerald-50 border-emerald-300',
    moderate: 'bg-amber-50 border-amber-300',
    weak: 'bg-red-50 border-red-300',
};

function OrgChartNode({ data }) {
    const { name, title, role_in_deal, disc_profile, disc_secondary,
        influence_score, engagement_score, relationship_strength,
        avatar_url, heatMapMode, onNodeClick } = data;

    let borderClass = 'border-stone-200';
    let bgClass = 'bg-white';

    if (heatMapMode === 'relationship') {
        bgClass = STRENGTH_BG[relationship_strength] || 'bg-stone-50 border-stone-200';
        borderClass = '';
    } else if (heatMapMode === 'engagement') {
        const score = engagement_score || 0;
        if (score >= 70) { bgClass = 'bg-emerald-50'; borderClass = 'border-emerald-300'; }
        else if (score >= 40) { bgClass = 'bg-amber-50'; borderClass = 'border-amber-300'; }
        else { bgClass = 'bg-red-50'; borderClass = 'border-red-300'; }
    } else if (role_in_deal) {
        borderClass = ROLE_COLORS[role_in_deal] || 'border-stone-200';
    }

    const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

    return (
        <div
            className={`rounded-xl border-2 ${borderClass} ${bgClass} px-4 py-3 w-52 shadow-sm cursor-pointer hover:shadow-md transition-shadow relative`}
            onClick={() => onNodeClick && onNodeClick(data)}
        >
            <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-stone-400 !border-stone-300" />
            <div className="flex items-center gap-2.5">
                {avatar_url ? (
                    <img src={avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                    <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-xs font-bold text-stone-600">
                        {initials}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-stone-900 truncate">{name}</span>
                        {disc_profile && <DISCBadge profile={disc_profile} secondary={disc_secondary} size="sm" />}
                    </div>
                    <div className="text-xs text-stone-500 truncate">{title || 'No title'}</div>
                </div>
            </div>
            <div className="flex items-center gap-1.5 mt-2">
                {role_in_deal && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 font-medium">{role_in_deal}</span>
                )}
                {influence_score != null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        influence_score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                        influence_score >= 4 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                    }`}>{influence_score}/10</span>
                )}
            </div>
            <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-stone-400 !border-stone-300" />
        </div>
    );
}

const nodeTypes = { orgNode: OrgChartNode };

// ─── Edge Styles ────────────────────────────────────────────────────────────

const EDGE_STYLES = {
    reports_to: { stroke: '#a8a29e', strokeWidth: 2, strokeDasharray: undefined },
    dotted_line: { stroke: '#a8a29e', strokeWidth: 1.5, strokeDasharray: '5 5' },
    influences: { stroke: '#3b82f6', strokeWidth: 2, strokeDasharray: undefined },
    blocks: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5 5' },
};

// ─── Layout ─────────────────────────────────────────────────────────────────

function getLayoutedElements(nodes, edges, direction = 'TB') {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: direction, nodesep: 60, ranksep: 100 });

    nodes.forEach(node => g.setNode(node.id, { width: 220, height: 90 }));
    edges.forEach(edge => g.setEdge(edge.source, edge.target));

    dagre.layout(g);

    return nodes.map(node => {
        const pos = g.node(node.id);
        return { ...node, position: { x: pos.x - 110, y: pos.y - 45 } };
    });
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function OrgChart({ orgNodes = [], accountId, onNodeClick, onSave }) {
    const { getAuthHeader } = useAuth();
    const [heatMapMode, setHeatMapMode] = useState(null); // null, 'relationship', 'engagement'
    const [autoConnecting, setAutoConnecting] = useState(false);

    // Convert org nodes to ReactFlow format
    const { initialNodes, initialEdges } = useMemo(() => {
        const rfNodes = orgNodes.map(n => ({
            id: n.node_id,
            type: 'orgNode',
            position: { x: n.position_x || 0, y: n.position_y || 0 },
            data: {
                ...n,
                heatMapMode,
                onNodeClick,
            },
        }));

        const rfEdges = orgNodes
            .filter(n => n.parent_node_id)
            .map(n => {
                const edgeStyle = EDGE_STYLES[n.relationship_type] || EDGE_STYLES.reports_to;
                return {
                    id: `${n.parent_node_id}-${n.node_id}`,
                    source: n.parent_node_id,
                    target: n.node_id,
                    type: 'smoothstep',
                    style: edgeStyle,
                    markerEnd: { type: MarkerType.ArrowClosed, width: 15, height: 15, color: edgeStyle.stroke },
                };
            });

        // Auto-layout if no positions set
        const hasPositions = rfNodes.some(n => n.position.x !== 0 || n.position.y !== 0);
        const layouted = hasPositions ? rfNodes : getLayoutedElements(rfNodes, rfEdges);

        return { initialNodes: layouted, initialEdges: rfEdges };
    }, [orgNodes, heatMapMode, onNodeClick]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Update nodes when heatMapMode changes
    React.useEffect(() => {
        setNodes(nds => nds.map(n => ({
            ...n,
            data: { ...n.data, heatMapMode },
        })));
    }, [heatMapMode, setNodes]);

    // Re-layout
    const handleAutoLayout = useCallback(() => {
        const layouted = getLayoutedElements(nodes, edges);
        setNodes(layouted);
    }, [nodes, edges, setNodes]);

    // Save org chart edges to backend
    const saveOrgChart = useCallback(async (edgesToSave) => {
        if (!accountId) return;
        try {
            await axios.put(`${API}/accounts/${accountId}`, {
                org_chart_edges: (edgesToSave || edges).map(e => ({
                    source: e.source,
                    target: e.target,
                    label: e.label || '',
                })),
            }, { headers: getAuthHeader() });
        } catch {
            // Silent fail — edge save is best-effort
        }
    }, [edges, accountId, getAuthHeader]);

    // Connect nodes by dragging from handle to handle
    const onConnect = useCallback((params) => {
        setEdges(eds => {
            const updated = addEdge({
                ...params,
                type: 'smoothstep',
                style: { stroke: '#a8a29e', strokeWidth: 2 },
                markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: '#a8a29e' },
            }, eds);
            // Save after state update
            setTimeout(() => saveOrgChart(updated), 0);
            return updated;
        });
    }, [setEdges, saveOrgChart]);

    // AI auto-connect based on job titles and seniority
    const autoConnectAI = async () => {
        const contactData = nodes.map(n => ({
            id: n.id,
            name: n.data.label || n.data.name,
            title: n.data.title || '',
            seniority: n.data.seniority || '',
        }));

        if (contactData.length < 2) {
            toast.info('Need at least 2 contacts to auto-connect');
            return;
        }

        try {
            setAutoConnecting(true);
            const res = await axios.post(`${API}/ai/org-chart-connect`, {
                contacts: contactData,
            }, { headers: getAuthHeader() });

            if (res.data.edges) {
                const newEdges = res.data.edges.map(e => ({
                    id: `ai_${e.from}_${e.to}`,
                    source: e.from,
                    target: e.to,
                    type: 'smoothstep',
                    style: { stroke: '#a8a29e', strokeWidth: 2 },
                    markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: '#a8a29e' },
                    label: e.label || 'reports to',
                }));
                setEdges(newEdges);
                saveOrgChart(newEdges);
                toast.success('AI connected org chart nodes');
            }
        } catch {
            toast.error('AI auto-connect failed');
        } finally {
            setAutoConnecting(false);
        }
    };

    // Save node positions on drag stop
    const handleNodeDragStop = useCallback((event, node) => {
        if (onSave) {
            // Debounced save could be added here
        }
    }, [onSave]);

    if (orgNodes.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 bg-stone-50 rounded-xl border border-stone-200">
                <p className="text-sm text-stone-400">No contacts in this account to display.</p>
            </div>
        );
    }

    return (
        <div className="h-[500px] bg-white rounded-xl border border-stone-200 overflow-hidden">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStop={handleNodeDragStop}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.3}
                maxZoom={2}
                attributionPosition="bottom-right"
            >
                <Background color="#e7e5e4" gap={20} size={1} />
                <Controls showInteractive={false} />
                <MiniMap
                    nodeColor={n => {
                        const d = n.data;
                        if (d?.disc_profile === 'D') return '#fb7185';
                        if (d?.disc_profile === 'I') return '#fbbf24';
                        if (d?.disc_profile === 'S') return '#34d399';
                        if (d?.disc_profile === 'C') return '#38bdf8';
                        return '#d6d3d1';
                    }}
                    maskColor="rgba(250, 250, 245, 0.7)"
                />

                {/* Toolbar */}
                <Panel position="top-right" className="flex gap-1.5">
                    <Button
                        variant="outline" size="sm"
                        onClick={autoConnectAI}
                        disabled={autoConnecting}
                        className="bg-white shadow-sm"
                    >
                        {autoConnecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        AI Connect
                    </Button>
                    <Button
                        variant="outline" size="sm"
                        onClick={handleAutoLayout}
                        className="bg-white shadow-sm"
                    >
                        <Layout className="w-3.5 h-3.5 mr-1" /> Auto Layout
                    </Button>
                    <Button
                        variant={heatMapMode === 'relationship' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setHeatMapMode(m => m === 'relationship' ? null : 'relationship')}
                        className={heatMapMode === 'relationship' ? '' : 'bg-white shadow-sm'}
                    >
                        <Thermometer className="w-3.5 h-3.5 mr-1" /> Relationship
                    </Button>
                    <Button
                        variant={heatMapMode === 'engagement' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setHeatMapMode(m => m === 'engagement' ? null : 'engagement')}
                        className={heatMapMode === 'engagement' ? '' : 'bg-white shadow-sm'}
                    >
                        <ZoomIn className="w-3.5 h-3.5 mr-1" /> Engagement
                    </Button>
                </Panel>
            </ReactFlow>
        </div>
    );
}
